'use client';
import { Row, Card, Stack } from '@/ui/primatives';
import Link from 'next/link';
import React from 'react';
import * as Styled from './styled';

import { toTimeAgo } from '@/lib/dateTime';
import { IRepository } from '@/types';
import { Tooltip } from 'react-tooltip';

import { ReaderIcon, LayersIcon } from '@radix-ui/react-icons';
import * as ListTab from '@/ui/ListTab';
import Tag from '@/ui/Tag';
import Button from '@/ui/Button';
import CopyTextControl from '@/ui/CopyTextControl';
import { IMarimoSandbox } from '@/lib/marimoSandboxesApi';
import useNotebooks from '@/hooks/useNotebook';
import UserTooltip from './UserTooltip';

const NoteBookCell: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <td>
      <Row alignItems="center" gap={10}>
        {children}
      </Row>
    </td>
  );
};

const NotebookRow: React.FC<{
  notebook: IMarimoSandbox;
  repository: IRepository;
  onStop: (notebookId: string) => void;
}> = ({ notebook, repository, onStop }) => {
  const handleStop = () => {
    if (window.confirm('Are you sure you want to stop this notebook?')) {
      onStop(notebook.id);
    }
  };

  return (
    <tr>
      <NoteBookCell>
        {notebook.filepath}
        <CopyTextControl
          id={`notebook-path-${notebook.id}`}
          value={notebook.filepath}
          assetType="path"
          event={{
            name: 'notebook_path_copied',
            payload: { notebook_id: notebook.id, filepath: notebook.filepath },
          }}
        />
      </NoteBookCell>
      <NoteBookCell>
        <Tag small variant={notebook.status === 'running' ? 'success' : 'default'}>
          {notebook.status}
        </Tag>
      </NoteBookCell>
      <NoteBookCell>
        {notebook.workspace && notebook.workspace.user && (
          <Link href={`/${notebook.workspace.user.username}`}>
            <UserTooltip user={notebook.workspace.user} notebook={notebook} />
          </Link>
        )}
        {toTimeAgo(notebook.inserted_at, { short: true })}
      </NoteBookCell>
      <NoteBookCell>
        {notebook.status === 'running' ? (
          <Link href={`/${repository.namespace}/${repository.name}/notebooks/${notebook.id}`} className="filename">
            <Tag small>
              <ReaderIcon height={12} />
              {notebook.id}
            </Tag>
          </Link>
        ) : (
          <Tag small>
            <ReaderIcon height={12} />
            <span className="truncate" style={{ maxWidth: '200px' }}>
              {notebook.id}
            </span>
          </Tag>
        )}
        <CopyTextControl
          id={`notebook-id-${notebook.id}`}
          assetType="id"
          value={notebook.id}
          event={{
            name: 'notebook_id_copied',
            payload: { notebook_id: notebook.id },
          }}
        />
      </NoteBookCell>
      <NoteBookCell>
        <Tag variant="primary" small>
          <LayersIcon height={12} />
          <span className="truncate" style={{ maxWidth: '200px' }}>
            {notebook.workspace.id}
          </span>
        </Tag>
        <CopyTextControl
          id={`notebook-workspace-id-${notebook.id}`}
          assetType="id"
          value={notebook.workspace.id}
          event={{
            name: 'notebook_workspace_id_copied',
            payload: { notebook_id: notebook.id, workspace_id: notebook.workspace.id },
          }}
        />
      </NoteBookCell>
      <NoteBookCell>
        <Tag variant="info" small>
          {`$ ${Number(notebook.credits_used).toFixed(4)}`}
        </Tag>
      </NoteBookCell>
      <NoteBookCell>
        {notebook.status === 'running' && (
          <Button.Success
            tiny
            href={`/${repository.namespace}/${repository.name}/notebooks/${notebook.id}`}
            style={{ fontSize: '12px' }}
          >
            Open
          </Button.Success>
        )}
        {notebook.status === 'running' && (
          <Button.Danger tiny onClick={handleStop} style={{ fontSize: '12px' }}>
            Stop
          </Button.Danger>
        )}
      </NoteBookCell>
      {/* <Tooltip
        id="notebook-info-tooltip"
        style={{ backgroundColor: 'var(--color-secondary-lightGray3)', color: '#000', zIndex: 1000 }}
      /> */}
    </tr>
  );
};

interface NotebookTableProps {
  headers: string[];
  notebooks: IMarimoSandbox[];
  repository: IRepository;
  onStop: (notebookId: string) => void;
}

const NotebookTable: React.FC<NotebookTableProps> = ({ headers, notebooks, repository, onStop }) => {
  if (!notebooks.length) {
    return (
      <Card.Alternate>
        <Stack.Centered>No notebooks found</Stack.Centered>
      </Card.Alternate>
    );
  }
  return (
    <Styled.NotebookList>
      <thead>
        <tr>
          {headers.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {notebooks.map((notebook) => (
          <NotebookRow key={notebook.id} notebook={notebook} repository={repository} onStop={onStop} />
        ))}
      </tbody>
    </Styled.NotebookList>
  );
};

const NotebookList: React.FC<{
  repository: IRepository;
  notebooks?: IMarimoSandbox[];
  stopNotebook: (notebookId: string) => void;
}> = ({ repository, notebooks = [], stopNotebook }) => {
  const notebookList = notebooks || [];
  const activeNotebooks = notebookList.filter((n) => n.status === 'running');
  const inactiveNotebooks = notebookList.filter((n) => n.status !== 'running');

  const headers = ['File Path', 'Status', 'Created', 'ID', 'Workspace', 'Cost', ''];

  const tableProps = {
    headers,
    repository,
    onStop: stopNotebook,
  };

  const defaultTab = activeNotebooks.length > 0 ? 'active' : inactiveNotebooks.length > 0 ? 'inactive' : 'all';
  const isEmpty = activeNotebooks.length === 0 && inactiveNotebooks.length === 0;

  return (
    <ListTab.ListTab defaultValue={defaultTab}>
      <ListTab.ListTabList>
        <ListTab.ListTabTrigger value="active" count={activeNotebooks.length}>
          Active
        </ListTab.ListTabTrigger>
        <ListTab.ListTabTrigger value="inactive" count={inactiveNotebooks.length}>
          Inactive
        </ListTab.ListTabTrigger>
        <ListTab.ListTabTrigger value="all">All</ListTab.ListTabTrigger>
      </ListTab.ListTabList>

      <ListTab.ListTabContent value="active">
        <NotebookTable {...tableProps} notebooks={activeNotebooks} />
      </ListTab.ListTabContent>

      <ListTab.ListTabContent value="all">
        {isEmpty ? (
          <Card.Alternate>
            <Stack.Centered>
              You have no notebooks
            </Stack.Centered>
          </Card.Alternate>
        ) : (
          <NotebookTable {...tableProps} notebooks={notebookList} />
        )}
      </ListTab.ListTabContent>

      <ListTab.ListTabContent value="inactive">
        <NotebookTable {...tableProps} notebooks={inactiveNotebooks} />
      </ListTab.ListTabContent>
    </ListTab.ListTab>
  );
};

const NotebookListLoader: React.FC<{ repository: IRepository; initialNotebooks?: IMarimoSandbox[] }> = ({
  repository,
  initialNotebooks = [],
}) => {
  const { notebooks, stopNotebook, isLoading, isValidating } = useNotebooks({
    repository,
    initialData: initialNotebooks,
  });

  return <NotebookList repository={repository} notebooks={notebooks} stopNotebook={stopNotebook} />;
};

export { NotebookList };
export default NotebookListLoader;
